import React, { useState } from "react";
import { generateClassName } from "../../../hooks/useAttributes";
import IElementProps from "../../../types/ElementProps";
import Button from "../buttons/Button";
import Icon from "../icons/Icon";
import LoadingSpinner from "../loader/LoadingSpinner";
import InfiniteScroll, { IInfiniteBase } from "../pagination/InfiniteScroll";
import ComboBoxItem from "./ComboBoxItem";
import Expandable from "./Expandable";
import "./SearchableComboBox.css";

type ComboBoxValue<T> = T | string | null;

export interface ISearchableComboBoxBase<T> extends IInfiniteBase, IElementProps {
  readOnly?: boolean;
  disabled?: boolean;
  noValuesPlaceholder?: string;
  filterPlaceholder?: string;
  fullWidth?: boolean;
  placeholder?: string;
  loadingText?: string;
  textOnEmpty?: string;
  highlightChecked?: boolean;
  allowAnyValue?: boolean;
  icon?: string;
  isHtml?: boolean;
  allowMultiple?: boolean;
  filter?: string;
  label?: string;
  cannotClearValue?: boolean;
  keepOpen?: boolean;
  preventChangeOnSelectedClick?: boolean;
  bold?: boolean;
  value?: (ComboBoxValue<T> | ComboBoxValue<T>[]),
  onChange: (value: T | undefined, all: string[]) => (Promise<any> | any);
  shouldItemRender?: (item: T) => boolean;
  alwaysShowPlaceholder?: boolean;
}

interface ISearchableComboBoxProps<T> extends ISearchableComboBoxBase<T> {
  values: T[];
  valuesById?: Record<string, T>;
  loading?: boolean;
  loadingFilter?: boolean;
  isFiltered?: boolean;
  expander?: React.ReactElement;
  additionalFilterComponents?: React.ReactNode;
  renderItemLikeValue?: boolean;
  renderItem?: (item: T) => React.ReactElement;
  filterFunction?: (item: T, filter: string) => boolean;
  handleFilterChange?: (value: string) => void;
  itemToId?: (value: T) => string;
  itemToString?: (value: T, existsInValues?: boolean) => string | number;
}

export default function SearchableComboBox<T>({
  className,
  loadingFilter,
  fullWidth,
  icon,
  isFiltered,
  cannotClearValue,
  highlightChecked,
  additionalFilterComponents,
  preventChangeOnSelectedClick,
  alwaysShowPlaceholder,
  values,
  valuesById,
  filter: externalFilter,
  filterPlaceholder,
  keepOpen,
  loadingText = "Loading...",
  textOnEmpty,
  isHtml,
  label,
  bold,
  allowAnyValue,
  readOnly,
  value,
  placeholder = "Select...",
  filterFunction,
  handleFilterChange,
  disabled,
  shouldItemRender,
  allowMultiple = false,
  loading,
  itemToId,
  renderItem,
  noValuesPlaceholder,
  expander,
  itemToString,
  onChange,
  itemsPerPage,
  onLoadMore,
  loadingMore,
  hasMore
}: ISearchableComboBoxProps<T>) {


  const toId = itemToId ?? ((v: T) => `${v}`);
  const toString = itemToString ?? ((v: T, _) => `${v}`);

  const [handleChangeId, setHandleChangeId] = useState<string | null>(null);
  const [handlingChange, setHandlingChange] = useState<boolean>(false);
  const [internalFilter, setInternalFilter] = useState<string>(externalFilter ?? "");

  const inputRef = React.useRef<HTMLInputElement>(null);
  const filterTimer = React.useRef<any>(null);

  const checkItemFilterMatch = (v: T) => {
    const filterToUpper = (internalFilter ?? "").toUpperCase().trim();

    if (filterFunction) return filterFunction(v, filterToUpper);
    const itemString = toString(v);
    if (itemString == null) return false;
    const itemStringUpper = `${itemString}`.toUpperCase().trim();
    return itemStringUpper.includes(filterToUpper);
  };

  const onFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    clearTimeout(filterTimer.current);

    if (handleFilterChange) filterTimer.current = setTimeout(() => {
      handleFilterChange(e.target.value);
    }, 350);

    setInternalFilter(e.target.value);
  }

  const resetFilter = () => {
    handleFilterChange?.("");
    setInternalFilter("");
  }

  const onFilterFocus = () => setInternalFilter("");


  const byId = React.useMemo(() => {
    return valuesById || Object.fromEntries(values.map(v => [toId(v), v]));
  }, [valuesById, values, toId]);

  const getValue = (v: ComboBoxValue<T>) => typeof v === "string" ? byId?.[v] : v;

  const getItemId = (v: ComboBoxValue<T>) => {
    const rv = getValue(v);
    return toId(rv as any);
  }

  const getItemLabel = (v: ComboBoxValue<T>) => {
    const rv = getValue(v);
    if (!rv) return "";

    const id = getItemId(rv);
    const existsInValues = !!byId?.[id];

    return toString(rv, existsInValues);
  }

  const filtered = React.useMemo(() => {
    if (!!handleFilterChange) return values;
    if (!values?.length) return [];
    if (!internalFilter) return values;
    return values.filter(checkItemFilterMatch);
  }, [values, internalFilter, handleFilterChange]);

  const selected = React.useMemo(() => {
    const result: Map<string, T> = new Map();

    if (!value) return result;

    let items = [];

    if (Array.isArray(value)) {
      if (allowMultiple) items = value;
      else items = [value[0]];
    }
    else items = [value];

    for (const v of items) {
      const rv = getValue(v);
      if (!rv) continue;
      result.set(getItemId(rv), rv);
    }

    return result;
  }, [value, byId, allowMultiple]);

  const getExpanderText = () => {
    if (!alwaysShowPlaceholder && selected.size && value) {
      const first = getItemLabel(Array.isArray(value) ? value[0] : value);

      if (!allowMultiple) return first;
      if (selected.size === 1) return first;

      return `${first} + ${selected.size - 1}`;
    }

    const p = placeholder ?? "Select...";
    if (loading) return loadingText ?? p ?? "Loading...";
    return !!filtered?.length ? p : textOnEmpty ?? p ?? "Select...";
  }

  const isEmpty = !isFiltered && !values?.length;
  const noResults = !filtered?.length;

  const displayNameClass = "searchable-combo-box-value-container form-control";

  const inputClass = generateClassName(displayNameClass, "searchable-combo-box-input", {
    base: "searchable-combo-box-input-",
    value: !noResults,
    onTrue: "expanded",
    standard: "collapsed"
  }, {
    value: !isEmpty && noResults,
    onTrue: "searchable-combo-box-no-results"
  });

  const itemsContainerClass = generateClassName("searchable-combo-box-item-container w-100 h-100", {
    value: !noResults,
    onTrue: "searchable-combo-box-item-container-with-values"
  });

  const comboBoxContainerClass = generateClassName("position-relative gap-1 d-flex flex-column", className);

  return (
    <div className={comboBoxContainerClass}>
      {
        label && <span className="form-label m-0 p-0">{label}</span>
      }
      <Expandable
        onClose={resetFilter}
        onOpen={() => setTimeout(() => inputRef?.current?.focus(), 0)}
        disabled={disabled || isEmpty || readOnly || loading}
        fullWidth={fullWidth}
        expander={expander ?? (
          <Button
            icon={icon ?? (allowMultiple ? "plus" : value ? "check" : "search")}
            iconPosition="start"
            variant={value ? "solid" : "subtle"}
            loading={loading}
            className={className}
          >
            {
              getExpanderText()
            }
          </Button>
        )}
      >
        {
          close => {


            const handleChange = async (v: T) => {

              setHandlingChange(true);
              setHandleChangeId(toId(v));

              try {
                const isSelected = selected.has(toId(v));

                if (!allowMultiple) {
                  if (isSelected) await onChange(undefined, []);
                  else await onChange(v, [toId(v)]);
                  return;
                }

                const nextSelected = new Map(selected);

                if (isSelected) nextSelected.delete(toId(v));
                else nextSelected.set(toId(v), v);

                if (!nextSelected.size) {
                  await onChange(undefined, []);
                  return;
                }

                const nextValue = Array.from(nextSelected.keys());

                await onChange(v, nextValue);
              }
              finally {
                setHandlingChange(false);
              }
            }


            const render = (v: T) => {
              const isSelected = selected.has(toId(v));

              return (
                <div className="d-flex flex-row gap-2">
                  {
                    (highlightChecked || allowMultiple || handlingChange) && (
                      <div style={{ width: "20px" }}>
                        <Icon
                          size={20}
                          icon={isSelected ? "check" : ""}
                          color="var(--teal-600)"
                          loading={handlingChange && toId(v) === handleChangeId}
                        />
                      </div>
                    )
                  }
                  {
                    renderItem
                      ? renderItem(v)
                      : (
                        <span className="ps-1 pe-1 fw-bold" style={{ textTransform: "uppercase", maxWidth: "70vw", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {toString(v)}
                        </span>
                      )
                  }
                </div>
              );
            }

            return (
              <div className="d-flex flex-column gap-1 w-100 h-100">
                <div className="d-flex flex-column align-items-start p-2 searchable-combo-box-input-container">
                  <div className="d-flex flex-row gap-2 w-100">
                    <input
                      ref={inputRef}
                      disabled={loadingFilter || disabled || isEmpty || readOnly}
                      readOnly={readOnly}
                      className={inputClass}
                      onFocus={onFilterFocus}
                      onChange={onFilterChange}
                      onKeyDownCapture={async (e) => {
                        if (e.key === "Escape") {
                          resetFilter();
                          inputRef.current?.blur();
                          return;
                        }

                        if (e.key === "Enter") {

                          if (filtered?.length === 1) {
                            await handleChange(filtered[0]);
                          }
                          else if (allowAnyValue) onChange(internalFilter as any, [internalFilter as any]);

                          resetFilter();
                          close();
                        }
                      }}
                      value={internalFilter}
                      placeholder={isEmpty ? (noValuesPlaceholder || "No Elements") : (filterPlaceholder ?? placeholder ?? "Search...")}
                    />
                    {
                      (loading || loadingFilter)
                        ? <LoadingSpinner />
                        : (
                          ((value && !cannotClearValue) || internalFilter) && (
                            <Button
                              onClick={async () => {
                                if (!internalFilter) await onChange(undefined, []);
                                else resetFilter();
                              }}
                              icon={internalFilter ? "x" : "trash"}
                              color="var(--bs-danger)"
                              variant="ghost"
                            />
                          )
                        )
                    }
                  </div>
                  {
                    additionalFilterComponents
                  }
                </div>
                <div className={itemsContainerClass}>
                  <div className="d-flex flex-column gap-1 searchable-combo-box-items w-100">
                    <InfiniteScroll
                      itemsPerPage={itemsPerPage}
                      loadingMore={loadingMore || loading}
                      hasMore={hasMore}
                      onLoadMore={onLoadMore}
                    >
                      {
                        !loading && (
                          !!filtered?.length
                            ? filtered.map((v: T) => {
                              if (shouldItemRender && !shouldItemRender(v)) return null;

                              return (
                                <ComboBoxItem
                                  key={toId(v)}
                                  content={render(v)}
                                  onClick={async () => {
                                    if (preventChangeOnSelectedClick && !!selected?.has(toId(v))) {
                                      return;
                                    }

                                    await handleChange(v)
                                    if (!keepOpen) {
                                      close();
                                    }
                                  }}
                                />
                              )
                            })
                            : <div className="ps-3">No Results.{allowAnyValue ? " Press Enter to add." : undefined}</div>
                        )
                      }
                    </InfiniteScroll>
                  </div>
                </div>
              </div>
            )
          }
        }
      </Expandable>
    </div>
  )
}